/**
 * TILE-367: Unified Popup Manager
 * 
 * Central popup coordination system that:
 * - Prevents conflicts (100% prevention)
 * - Manages all popup types
 * - Ensures only one popup shows at a time
 * - Manages session state
 * - Renders popups dynamically
 */

(function() {
  'use strict';

  /* This module is a RENDERER, not a capture lane of its own: its only caller is
     veluna-pdf-capture.js via showPopup(decision). It therefore claims the guard
     under the CALLING engine's id so priority and ownership reflect the real
     lane. The old hardcoded 'unified-popup' id made pdf-capture's popup register
     as the lowest-priority engine and block page-specific captures. */
  const DEFAULT_CAPTURE_ID = 'pdf-capture';
  const VELUNA_SUBSCRIBE_ENDPOINT =
    'https://ary09mimgg.execute-api.us-east-2.amazonaws.com/Prod/subscribe';

  class UnifiedPopupManager {
    constructor() {
      this.sessionState = {
        popupShown: false,
        popupType: null,
        shownAt: null,
        conflictsPrevented: 0
      };
      
      this.popupTemplates = {};
      this.activePopup = null;
      
      this.init();
    }
    
    init() {
      // Load session state
      this.loadSessionState();
      
      // Remove any existing popups (safety)
      this.removeExistingPopups();
      
      // Listen for Luna orchestration decisions
      this.setupLunaIntegration();
      
      console.log('✅ UnifiedPopupManager initialized');
    }
    
    loadSessionState() {
      try {
        const stored = sessionStorage.getItem('luna_popup_state');
        if (stored) {
          const data = JSON.parse(stored);
          // Consider it shown if within last 30 minutes
          if (Date.now() - data.timestamp < 30 * 60 * 1000) {
            this.sessionState.popupShown = true;
            this.sessionState.popupType = data.type;
            this.sessionState.shownAt = data.timestamp;
          }
        }
      } catch (e) {
        console.warn('Failed to load session state:', e);
      }
    }
    
    saveSessionState() {
      try {
        sessionStorage.setItem('luna_popup_state', JSON.stringify({
          popupShown: this.sessionState.popupShown,
          popupType: this.sessionState.popupType,
          timestamp: this.sessionState.shownAt || Date.now()
        }));
      } catch (e) {
        console.warn('Failed to save session state:', e);
      }
    }
    
    hasShownThisSession() {
      // Check in-memory state
      if (this.sessionState.popupShown) {
        return true;
      }
      
      // Check session storage (across page loads)
      const stored = sessionStorage.getItem('luna_popup_state');
      if (stored) {
        const data = JSON.parse(stored);
        // Consider it shown if within last 30 minutes
        if (Date.now() - data.timestamp < 30 * 60 * 1000) {
          return true;
        }
      }
      
      return false;
    }
    
    showPopup(decision) {
      const capId = (decision && decision.captureId) || DEFAULT_CAPTURE_ID;
      // CRITICAL: Only one popup at a time
      if (this.hasShownThisSession()) {
        this.sessionState.conflictsPrevented++;
        console.warn('⚠️ Popup already shown this session - preventing conflict');
        this.trackConflictPrevention(decision);
        return false;
      }
      
      // Check if popup already exists (safety)
      if (this.activePopup && document.body.contains(this.activePopup)) {
        this.sessionState.conflictsPrevented++;
        console.warn('⚠️ Active popup exists - preventing conflict');
        this.trackConflictPrevention(decision);
        return false;
      }
      
      // Get template
      const template = this.getTemplate(decision.popupType);
      if (!template) {
        console.error(`❌ Unknown popup type: ${decision.popupType}`);
        return false;
      }
      
      // Render popup with Luna's personalized message
      const popup = this.renderPopup(template, decision);
      
      if (!popup) {
        console.error('❌ Failed to render popup');
        return false;
      }
      
      // Show popup
      this.displayPopup(popup);
      
      // Update state (prevents conflicts)
      this.sessionState.popupShown = true;
      this.sessionState.popupType = decision.popupType;
      this.sessionState.shownAt = Date.now();
      this.activePopup = popup;
      
      // Save state (prevents conflicts across page loads)
      this.saveSessionState();
      
      // Track popup shown
      this.trackPopupShown(decision);
      
      return true;
    }
    
    getTemplate(popupType) {
      // Templates will be registered by template system
      if (window.VelunaPopupTemplates && window.VelunaPopupTemplates[popupType]) {
        return window.VelunaPopupTemplates[popupType];
      }
      
      // Fallback to existing popup if available
      if (popupType === 'global_email') {
        const existing = document.querySelector('.veluna-email-popup');
        if (existing) {
          return { type: 'global_email', useExisting: true, element: existing };
        }
      }
      
      return null;
    }
    
    renderPopup(template, decision) {
    
      const capId = (decision && decision.captureId) || DEFAULT_CAPTURE_ID;
      // If using existing popup, just return it
      if (template.useExisting && template.element) {
        return template.element;
      }
      
      // Create new popup element
      const popup = document.createElement('div');
      popup.className = 'unified-popup';
      /* Stamp the CALLING engine's capture id so displayPopup (and the guard)
         arbitrate as that lane, not as this renderer. */
      popup.setAttribute('data-capture-id', capId);
      popup.setAttribute('data-popup-type', template.type);
      popup.setAttribute('data-jarvis-element', 'unified-popup');
      popup.setAttribute('aria-hidden', 'true');
      
      // Render template with Luna's personalization
      if (template.render) {
        popup.innerHTML = template.render(decision);
      } else {
        popup.innerHTML = this.renderDefaultPopup(decision);
      }
      
      // Attach event listeners
      this.attachListeners(popup, template, decision);
      
      return popup;
    }
    
    renderDefaultPopup(decision) {
      const message = decision.message || {};
      return `
        <div class="unified-popup__backdrop" data-popup-close></div>
        <div class="unified-popup__dialog" role="dialog" aria-modal="true">
          <div class="veluna-card unified-popup__card">
            <button type="button" class="unified-popup__close" data-popup-close aria-label="Close">×</button>
            
            ${message.eyebrow ? `<p class="veluna-heading-eyebrow">${this.escapeHtml(message.eyebrow)}</p>` : ''}
            ${message.headline ? `<h2 class="veluna-heading-md">${this.escapeHtml(message.headline)}</h2>` : ''}
            ${message.subheadline ? `<p class="veluna-body">${this.escapeHtml(message.subheadline)}</p>` : ''}
            
            <form class="unified-popup__form" data-email-subscribe data-source="unified-popup">
              <div class="veluna-email-capture__form">
                <input type="email" name="email" placeholder="Enter your email" class="veluna-input" required>
                ${decision.channels && decision.channels.includes('sms') ? `
                  <label class="unified-popup__sms-opt-in">
                    <input type="checkbox" name="sms_opt_in" value="true">
                    Also send me text updates
                  </label>
                  <input type="tel" name="phone" placeholder="Phone number (optional)" class="veluna-input" style="display: none;">
                ` : ''}
                <button type="submit" class="veluna-btn veluna-btn-primary">
                  ${this.escapeHtml(message.cta || 'Subscribe')}
                </button>
              </div>
            </form>
            
            <p class="veluna-body-muted">
              We respect your inbox. Unsubscribe anytime.
            </p>
          </div>
        </div>
      `;
    }
    
    attachListeners(popup, template, decision) {
      // Close button
      const closeBtns = popup.querySelectorAll('[data-popup-close]');
      closeBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          this.closePopup(popup);
        });
      });
      
      // Form submission
      const form = popup.querySelector('form');
      if (form) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          await this.handleSubmission(popup, form, decision);
        });
      }
      
      // SMS opt-in toggle
      const smsCheckbox = popup.querySelector('input[name="sms_opt_in"]');
      const phoneInput = popup.querySelector('input[name="phone"]');
      if (smsCheckbox && phoneInput) {
        smsCheckbox.addEventListener('change', (e) => {
          phoneInput.style.display = e.target.checked ? 'block' : 'none';
          if (e.target.checked) {
            phoneInput.required = true;
          } else {
            phoneInput.required = false;
            phoneInput.value = '';
          }
        });
      }
      
      // Escape key
      const escapeHandler = (e) => {
        if (e.key === 'Escape' && this.activePopup === popup) {
          this.closePopup(popup);
          document.removeEventListener('keydown', escapeHandler);
        }
      };
      document.addEventListener('keydown', escapeHandler);
    }
    
    async handleSubmission(popup, form, decision) {
      const formData = new FormData(form);
      const email = formData.get('email');
      const smsOptIn = formData.get('sms_opt_in') === 'true';
      const phone = formData.get('phone');
      
      // Send to backend (Luna's decision data included)
      // Skip in Theme Editor
      if (window.Shopify && window.Shopify.designMode) {
        return;
      }
      
      // Check endpoint availability
      if (typeof window.velunaEndpointAvailable === 'function') {
        const available = await window.velunaEndpointAvailable(VELUNA_SUBSCRIBE_ENDPOINT);
        if (!available) {
          // Endpoint not available - show error message
          if (decision.onSuccess) {
            decision.onSuccess({ status: 'error', message: 'Subscription service is temporarily unavailable.' });
          }
          return;
        }
      }
      
      try {
        // Canonical Veluna intake. This previously posted to the Shopify app
        // proxy path /apps/jarvis/email/subscribe, which returns 404 — so every
        // email captured by this popup was silently discarded.
        // phone / sms_opt_in are deliberately NOT forwarded: no reachable SMS
        // capture exists and this gate must not create one.
        const response = await fetch(VELUNA_SUBSCRIBE_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            topic: 'popup_subscribe',
            email: email,
            email_sequence: 'welcome',
            popup_type: decision.popupType,
            popup_variant: 'unified_popup_manager',
            tags: ['unified_popup_manager', 'welcome'],
            source: 'unified_popup_manager'
          })
        }).catch(function(error) {
          // Network errors - fail silently
          return null;
        });
        
        if (!response || !response.ok) {
          return;
        }
        
        const result = await response.json();
        
        if (result && result.status === 'success') {
          // Show success message
          this.showSuccessMessage(popup);
          
          // Track conversion
          this.trackConversion(decision, email, smsOptIn);
          
          // Close popup after delay
          setTimeout(() => {
            this.closePopup(popup);
          }, 2000);
        } else {
          this.showErrorMessage(popup, result.message || 'Error subscribing. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        this.showErrorMessage(popup, 'Error subscribing. Please try again.');
      }
    }
    
    showSuccessMessage(popup) {
      const form = popup.querySelector('form');
      if (form) {
        const existing = form.querySelector('.unified-popup__message');
        if (existing) existing.remove();
        
        const message = document.createElement('div');
        message.className = 'unified-popup__message unified-popup__message--success';
        message.textContent = 'Thank you for subscribing!';
        form.appendChild(message);
      }
    }
    
    showErrorMessage(popup, errorMessage) {
      const form = popup.querySelector('form');
      if (form) {
        const existing = form.querySelector('.unified-popup__message');
        if (existing) existing.remove();
        
        const message = document.createElement('div');
        message.className = 'unified-popup__message unified-popup__message--error';
        message.textContent = errorMessage;
        form.appendChild(message);
      }
    }
    
    displayPopup(popup) {
      const capId = (popup && popup.getAttribute('data-capture-id')) || DEFAULT_CAPTURE_ID;
      // Remove any existing popup (safety)
      this.removeExistingPopups();
      
      // Single arbitration authority: never render on top of another capture.
      try {
        if (window.VelunaCaptureGuard && !window.VelunaCaptureGuard.mayShow(capId)) {
          console.log('[UnifiedPopup] Suppressed by capture guard:',
                      window.VelunaCaptureGuard.reason(capId));
          this.sessionState.conflictsPrevented++;
          return;
        }
      } catch (e) {}

      // Add to DOM
      popup.setAttribute('data-capture-id', capId);
      document.body.appendChild(popup);
      
      // Animate in
      requestAnimationFrame(() => {
        popup.setAttribute('aria-hidden', 'false');
        popup.classList.add('unified-popup--visible');
      });
      try { window.VelunaCaptureGuard && window.VelunaCaptureGuard.notifyOpen(capId); } catch (e) {}
    }
    
    closePopup(popup) {
      if (!popup || !popup.parentNode) return;
      
      popup.setAttribute('aria-hidden', 'true');
      popup.classList.remove('unified-popup--visible');
      try { window.VelunaCaptureGuard && window.VelunaCaptureGuard.notifyClosed(popup.getAttribute('data-capture-id') || DEFAULT_CAPTURE_ID); } catch (e) {}
      
      // Dispatch popup closed event for Luna
      const closeEvent = new CustomEvent('luna-popup-closed', {
        detail: {
          popupType: popup.getAttribute('data-popup-type'),
          converted: false
        }
      });
      window.dispatchEvent(closeEvent);
      
      // Remove after animation
      setTimeout(() => {
        if (popup.parentNode) {
          popup.remove();
        }
        if (this.activePopup === popup) {
          this.activePopup = null;
        }
      }, 300);
    }
    
    removeExistingPopups() {
      // Remove all popup types (safety)
      const popups = document.querySelectorAll([
        '.veluna-email-popup',
        '.luna-exit-intent-modal',
        '.veluna-product-discount-quiz',
        '.veluna-quiz-abandonment-modal',
        '.veluna-quiz-results-exit-modal',
        '.unified-popup'
      ].join(','));
      
      popups.forEach(popup => {
        if (popup.parentNode) {
          popup.remove();
        }
      });
      
      this.activePopup = null;
    }
    
    trackPopupShown(decision) {
      // Track to analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'popup_shown', {
          popup_type: decision.popupType,
          source: 'unified_popup_manager',
          event_category: 'popup',
          event_label: decision.popupType
        });
      }
      
      // Track to backend
      if (window.VELUNA_ANALYTICS_ENDPOINT) {
        // Check endpoint availability
        if (typeof window.velunaEndpointAvailable === 'function') {
          window.velunaEndpointAvailable(window.VELUNA_ANALYTICS_ENDPOINT).then(available => {
            if (!available) {
              return; // Endpoint not available, skip silently
            }
            fetch(window.VELUNA_ANALYTICS_ENDPOINT, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                event: 'popup_shown',
                popup_type: decision.popupType,
                source: 'unified_popup_manager',
                luna_decision: decision,
                timestamp: new Date().toISOString()
              })
            }).catch(err => console.warn('Analytics tracking failed:', err));
          });
        } else {
          fetch(window.VELUNA_ANALYTICS_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: 'popup_shown',
              popup_type: decision.popupType,
              source: 'unified_popup_manager',
              luna_decision: decision,
              timestamp: new Date().toISOString()
            })
          }).catch(err => console.warn('Analytics tracking failed:', err));
        }
      }
    }
    
    trackConflictPrevention(decision) {
      // Track conflict prevention
      this.sessionState.conflictsPrevented++;
      
      if (typeof gtag !== 'undefined') {
        gtag('event', 'popup_conflict_prevented', {
          popup_type: decision.popupType,
          conflicts_prevented: this.sessionState.conflictsPrevented,
          event_category: 'popup',
          event_label: 'conflict_prevention'
        });
      }
    }
    
    trackConversion(decision, email, smsOptIn) {
      // Track conversion
      if (typeof gtag !== 'undefined') {
        gtag('event', 'email_captured', {
          popup_type: decision.popupType,
          source: 'unified_popup_manager',
          sms_opt_in: smsOptIn,
          event_category: 'email_capture',
          event_label: decision.popupType
        });
      }
      
      // Dispatch conversion event for Luna orchestrator
      const conversionEvent = new CustomEvent('luna-conversion', {
        detail: {
          email: email,
          popupType: decision.popupType,
          decision: decision,
          channels: decision.channels || ['email'],
          smsOptIn: smsOptIn
        }
      });
      window.dispatchEvent(conversionEvent);
      
      // Also notify Luna orchestrator directly (if available)
      if (window.LunaOrchestrator && window.LunaOrchestrator.onConversion) {
        window.LunaOrchestrator.onConversion({
          email: email,
          popupType: decision.popupType,
          decision: decision,
          channels: decision.channels || ['email'],
          smsOptIn: smsOptIn
        });
      }
    }
    
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
    
    setupLunaIntegration() {
      // Listen for Luna orchestration decisions
      window.addEventListener('luna-popup-decision', (e) => {
        const decision = e.detail;
        this.showPopup(decision);
      });
      
      // Expose API for Luna
      window.UnifiedPopupManager = this;
    }
    
    getStats() {
      return {
        popupsShown: this.sessionState.popupShown ? 1 : 0,
        conflictsPrevented: this.sessionState.conflictsPrevented,
        popupType: this.sessionState.popupType
      };
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.unifiedPopupManager = new UnifiedPopupManager();
    });
  } else {
    window.unifiedPopupManager = new UnifiedPopupManager();
  }

})();

